import { Injectable } from "@angular/core";
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";

import { CurrentService } from "app/services/current.service";
import { RoleService } from "app/services/role.service";

@Injectable({ providedIn: "root" })
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private currentService: CurrentService,
    private roleService: RoleService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this.currentService.currentUser.value;

    console.log(`🔐 AuthGuard - Vérification pour: ${state.url}`);

    if (currentUser?.refreshToken) {
      const requiredRoles = route.data.roles;
      const requiredPermission = route.data.permission;
      const requiredPermissions = route.data.permissions;
      const currentPermissions = this.roleService.getCurrentPermissions();

      console.log(`🔐 AuthGuard - Route data:`, {
        requiredRoles,
        requiredPermission,
        requiredPermissions,
        userRole: currentUser.role,
        currentPermissions: currentPermissions,
        permissionsCount: currentPermissions.length,
      });

      // Si des permissions sont requises (nouveau système)
      const hasPermissionRequirement =
        requiredPermission || (requiredPermissions && requiredPermissions.length > 0);

      // Si des rôles sont requis (ancien système)
      const hasRoleRequirement = requiredRoles && requiredRoles.length > 0;

      // Vérifier les permissions si définies
      let hasPermissionAccess = false;
      if (requiredPermission) {
        hasPermissionAccess = this.roleService.hasPermission(requiredPermission);
        console.log(
          `🔐 AuthGuard - Permission "${requiredPermission}" check: ${hasPermissionAccess}`
        );
      }
      if (requiredPermissions && requiredPermissions.length > 0) {
        const checkMode = route.data.checkMode || "any";
        if (checkMode === "all") {
          hasPermissionAccess = this.roleService.hasAllPermissions(requiredPermissions);
        } else {
          hasPermissionAccess = this.roleService.hasAnyPermission(requiredPermissions);
        }
        console.log(
          `🔐 AuthGuard - Permissions [${requiredPermissions.join(
            ", "
          )}] check (${checkMode}): ${hasPermissionAccess}`
        );
      }

      // Vérifier les rôles si définis
      let hasRoleAccess = false;
      if (hasRoleRequirement) {
        hasRoleAccess = requiredRoles.includes(currentUser.role);
        console.log(
          `🔐 AuthGuard - Role ${currentUser.role} in [${requiredRoles.join(
            ", "
          )}]: ${hasRoleAccess}`
        );
      }

      console.log(`🔐 AuthGuard - Résultat:`, {
        hasPermissionRequirement,
        hasRoleRequirement,
        hasPermissionAccess,
        hasRoleAccess,
      });

      // Logique de décision:
      // - Si les deux systèmes sont définis, autoriser si l'un des deux est valide
      // - Si seul un système est défini, utiliser ce système
      // - Si aucun système n'est défini, autoriser
      if (hasPermissionRequirement && hasRoleRequirement) {
        // Période de transition: l'un OU l'autre
        if (!hasPermissionAccess && !hasRoleAccess) {
          console.log(`🚫 AuthGuard - Accès refusé (permission ET rôle échoués)`);
          this.router.navigate(["/miscellaneous/not-authorized"]);
          return false;
        }
      } else if (hasRoleRequirement && !hasRoleAccess) {
        // Uniquement ancien système
        console.log(`🚫 AuthGuard - Accès refusé (rôle uniquement)`);
        this.router.navigate(["/miscellaneous/not-authorized"]);
        return false;
      } else if (hasPermissionRequirement && !hasPermissionAccess) {
        // Uniquement nouveau système
        console.log(`🚫 AuthGuard - Accès refusé (permission uniquement)`);
        this.router.navigate(["/miscellaneous/not-authorized"]);
        return false;
      }

      console.log(`✅ AuthGuard - Accès autorisé à ${state.url}`);
      return true;
    }

    // ✅ FIX : Utiliser state.url au lieu de this.router.url
    const returnUrl = state.url;

    // ✅ Éviter les redirections infinies
    if (returnUrl.includes("/authentication/login")) {
      this.router.navigate(["/authentication/login"]);
      return false;
    }

    this.router.navigate(["/authentication/login"], {
      queryParams: {
        returnUrl: returnUrl,
        tenantId: this.currentService.currentTenant?.value?.id,
      },
    });

    return false;
  }
}
